/**
 * useTemplateMutations — CRUD hooks for the presentation-templates editor.
 *
 * Each mutation invalidates the ``["templates", ...]`` cache slot so the
 * Studio drawer + editor surface stay in sync after a create / update /
 * delete. We do NOT invalidate ``["templates", "preferences"]`` because
 * the resolved-preference slot is independent of CRUD (changes only when
 * the user explicitly switches default via ``useSetTemplatePreference``).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createTemplate,
  deleteTemplate,
  getTemplate,
  updateTemplate,
  type CreateTemplateBody,
  type TemplateDetail,
  type UpdateTemplateBody,
} from "@ando/core";

export const templateDetailQueryKey = (slug: string) =>
  ["templates", "detail", slug] as const;

/**
 * Fetch a single template's detail (incl. spec JSON). Used by the editor
 * route to populate the form. ``enabled`` defaults to ``true`` but call
 * sites can disable it (e.g. while no template is selected).
 */
export function useTemplateDetail(slug: string | null) {
  return useQuery<TemplateDetail>({
    queryKey: templateDetailQueryKey(slug ?? ""),
    queryFn: () => getTemplate(slug as string),
    enabled: slug != null && slug.length > 0,
    staleTime: 60_000,
  });
}

/**
 * Invalidate every templates list slot ("templates" + each kind) so the
 * next render fetches fresh data. We don't try to do an optimistic patch
 * because the BE response is the source of truth for ``slug``, ``scope``,
 * and ``preview_url`` — all of which can shift on update.
 */
function useInvalidateTemplates() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["templates"] });
  };
}

export function useCreateTemplate() {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: (body: CreateTemplateBody) => createTemplate(body),
    onSuccess: () => {
      invalidate();
    },
  });
}

export function useUpdateTemplate() {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: ({ slug, body }: { slug: string; body: UpdateTemplateBody }) =>
      updateTemplate(slug, body),
    onSuccess: () => {
      invalidate();
    },
  });
}

export function useDeleteTemplate() {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: (slug: string) => deleteTemplate(slug),
    onSuccess: () => {
      invalidate();
    },
  });
}
